import type { PlayerTrack } from "@gerama/stores";
import type { Song, Video, WidgetSectionItem } from "@gerama/types";
import { browserCoreApi } from "@/lib/api";

/** "Artist A، Artist B" — what sits under a title everywhere (rows, mini-player, overlay). */
export function artistNames(artists: { name: string }[] | undefined): string | undefined {
  const names = (artists ?? []).map((a) => a.name);
  return names.length ? names.join("، ") : undefined;
}

/** Builds a `PlayerTrack` from a catalogue `Song`, resolving its real HLS stream URL. The subtitle is the credited artists (never the queue's label — that's `PlayerContext`). */
export function songToTrack(song: Song): PlayerTrack {
  return {
    kind: "song",
    id: song.id,
    code: song.code,
    title: song.name,
    subtitle: artistNames(song.artists),
    artists: song.artists?.map((a) => ({ id: a.id, name: a.name, code: a.code })),
    cover: song.cover,
    streamUrl: browserCoreApi.songs.streamUrl(song.code || song.id),
    durationSeconds: song.duration,
  };
}

export function videoToTrack(video: Video): PlayerTrack {
  return {
    kind: "video",
    id: video.id,
    code: video.code,
    title: video.name,
    subtitle: artistNames(video.artists),
    artists: video.artists?.map((a) => ({ id: a.id, name: a.name, code: a.code })),
    relatedSongId: video.songId,
    cover: video.cover,
    streamUrl: browserCoreApi.videos.streamUrl(video.code || video.id),
    durationSeconds: video.duration,
  };
}

/**
 * A home-feed item (song/video card, or a slide/banner/featured item that
 * points at one) → a playable track. The stream URL only needs the id, so
 * playback starts without fetching the song first.
 */
export function widgetItemToTrack(item: WidgetSectionItem): PlayerTrack | null {
  const kind = item.target?.type ?? item.type;
  const id = item.target?.id ?? (typeof item.id === "number" ? item.id : null);
  if ((kind !== "song" && kind !== "video") || id == null) return null;
  return {
    kind,
    id,
    code: item.target?.code,
    title: item.title,
    subtitle: item.subtitle ?? undefined,
    cover: item.image ?? null,
    streamUrl:
      kind === "song"
        ? browserCoreApi.songs.streamUrl(item.target?.code || id)
        : browserCoreApi.videos.streamUrl(item.target?.code || id),
    durationSeconds: item.duration ?? 0,
  };
}

/** A podcast episode → a playable track (streams from `/podcasts/episodes/:id/stream`). */
export function episodeToTrack(
  episode: {
    id: number;
    name: string;
    cover: string | null;
    duration: number;
    progress?: { positionSeconds: number; completed: boolean } | null;
  },
  podcastName?: string,
): PlayerTrack {
  return {
    kind: "episode",
    id: episode.id,
    title: episode.name,
    subtitle: podcastName,
    cover: episode.cover,
    streamUrl: browserCoreApi.podcasts.streamUrl(episode.id),
    durationSeconds: episode.duration,
    // Pick up where the listener stopped (a finished episode starts over).
    startAt:
      episode.progress && !episode.progress.completed && episode.progress.positionSeconds > 5
        ? episode.progress.positionSeconds
        : undefined,
  };
}
